import { combineLatest } from "rxjs";
import { resetPermalinkAffectingSettings, supportsPermalinking } from "./Settings";
import { diffLeftSelectedMinecraftVersion, diffView, selectedFile, selectedLines, selectedMinecraftVersion } from "./State";
import { toClassFilePath, withoutClassExtension, type ClassFilePath } from "../utils/Names";

export interface State {
    version: number; // Allows us to change the permalink structure in the future
    minecraftVersion: string;
    file: ClassFilePath | undefined;
    selectedLines: {
        line: number;
        lineEnd?: number;
    } | null;
    diff?: {
        leftMinecraftVersion: string;
    };
}

const DEFAULT_STATE: State = {
    version: 0,
    minecraftVersion: "",
    file: undefined,
    selectedLines: null
};

export const parsePathToState = (path: string): State | null => {
    // Check for line number marker (e.g., #L123 or #L10-20)
    let lineNumber: number | null = null;
    let lineEnd: number | null = null;
    const lineMatch = path.match(/(?:#|%23)L(\d+)(?:-(\d+))?$/);
    if (lineMatch) {
        lineNumber = parseInt(lineMatch[1], 10);
        if (lineMatch[2]) {
            lineEnd = parseInt(lineMatch[2], 10);
        }
        path = path.substring(0, lineMatch.index);
    }

    const segments = path.split('/').filter(s => s.length > 0);

    if (segments.length < 2) {
        return null;
    }

    const version = parseInt(segments[0], 10);

    if (segments[1] === 'diff') {
        if (segments.length < 4) {
            return null;
        }
        const leftMinecraftVersion = decodeURIComponent(segments[2]);
        const rightMinecraftVersion = decodeURIComponent(segments[3]);
        const filePath = segments.slice(4).join('/');
        return {
            version,
            minecraftVersion: rightMinecraftVersion,
            file: filePath ? toClassFilePath(filePath) : undefined,
            selectedLines: null,
            diff: { leftMinecraftVersion }
        };
    }

    let minecraftVersion = decodeURIComponent(segments[1]);
    const filePath = segments.slice(2).join('/');

    // Backwards compatibility with the incorrect version name used previously
    if (minecraftVersion == "25w45a") {
        minecraftVersion = "25w45a_unobfuscated";
    }

    return {
        version,
        minecraftVersion,
        file: filePath ? toClassFilePath(filePath) : undefined,
        selectedLines: lineNumber ? { line: lineNumber, lineEnd: lineEnd || undefined } : null
    };
};

export const getInitialState = (): State => {
    const hash = window.location.hash;

    let path = hash.startsWith('#/') ? hash.slice(2) : (hash.startsWith('#') ? hash.slice(1) : '');

    try {
        const state = parsePathToState(path);
        if (state === null) {
            return DEFAULT_STATE;
        }

        resetPermalinkAffectingSettings();
        return state;
    } catch (e) {
        console.error("Error parsing permalink:", e);
        return DEFAULT_STATE;
    }
};

if (typeof window !== "undefined") {
    window.addEventListener('load', () => {
        combineLatest([
            selectedMinecraftVersion,
            diffLeftSelectedMinecraftVersion,
            selectedFile,
            selectedLines,
            supportsPermalinking,
            diffView
        ]).subscribe(([
            minecraftVersion,
            diffLeftMinecraftVersion,
            file,
            selectedLines,
            supported,
            diffView
        ]) => {
            if (!file && !diffView) {
                document.title = "mcsrc.dev";
                window.location.hash = '';
                return;
            }

            if (file) {
                const className = withoutClassExtension(file.split('/').pop() || file);
                document.title = className;
            } else {
                document.title = "mcsrc.dev";
            }

            if (!supported) {
                window.location.hash = '';
                return;
            }

            let url = '1/';

            if (diffView) {
                url += `diff/${diffLeftMinecraftVersion}/${minecraftVersion}`;
                if (file) {
                    url += `/${withoutClassExtension(file)}`;
                }
            } else {
                url += `${minecraftVersion}/${withoutClassExtension(file!)}`;

                if (selectedLines) {
                    const { line, lineEnd } = selectedLines;
                    if (lineEnd && lineEnd !== line) {
                        url += `#L${Math.min(line, lineEnd)}-${Math.max(line, lineEnd)}`;
                    } else {
                        url += `#L${line}`;
                    }
                }
            }

            window.location.hash = url;
        });
    });
}
